import api from './client'

export type AccountType =
  | 'BANK'
  | 'CASH'
  | 'DIGITAL_WALLET'
  | 'INVESTMENT'
  | 'RECEIVABLE'
  | 'PROPERTY'
  | 'LOAN'
  | 'CREDIT_CARD'
  | 'PAYABLE'

/** Types that move money through the generic Expense/Income/Transfer pipeline and can be
 *  balance-adjusted. LOAN (dedicated repayment endpoints) and PROPERTY (static purchase-value
 *  placeholder, no value-tracking yet) are excluded. */
export const TRANSACTABLE_TYPES: AccountType[] = [
  'BANK',
  'CASH',
  'DIGITAL_WALLET',
  'INVESTMENT',
  'CREDIT_CARD',
  'RECEIVABLE',
  'PAYABLE',
]

export function isTransactable(type: AccountType): boolean {
  return TRANSACTABLE_TYPES.includes(type)
}

/** Plain accounts a goal can link to for DEDICATED/EARMARK tracking. */
export const PLAIN_ACCOUNT_TYPES: AccountType[] = ['BANK', 'CASH', 'INVESTMENT']

export function isPlainAccount(type: AccountType): boolean {
  return PLAIN_ACCOUNT_TYPES.includes(type)
}

/** Types that reduce net worth — colored/signed as liabilities everywhere they're shown. */
export const LIABILITY_TYPES: AccountType[] = ['LOAN', 'CREDIT_CARD', 'PAYABLE']

export function isLiabilityType(type: AccountType): boolean {
  return LIABILITY_TYPES.includes(type)
}

/** Types that track a manually-updated current market value separate from the
 *  transaction-driven balance (INVESTMENT contributions, PROPERTY's frozen purchase price). */
export const VALUATION_TRACKABLE_TYPES: AccountType[] = ['INVESTMENT', 'PROPERTY']

/** account.type and account.subType are SCREAMING_SNAKE_CASE enum values on the wire
 *  (e.g. "CREDIT_CARD", "MARKET_LINKED") — this is the only place that's meant to be
 *  read directly, not compared against. */
export function formatAccountTypeLabel(value: string): string {
  return value.replace(/_/g, ' ')
}

export function supportsValuationHistory(type: AccountType): boolean {
  return VALUATION_TRACKABLE_TYPES.includes(type)
}

export interface AccountSummary {
  id: string
  name: string
  icon?: string | null
  type: AccountType
  subType: string | null
  balance: number
  openingBalance: number
  creditLimit: number | null
  availableCredit: number | null
  ownerMemberIds: string[]
  createdAt: string
  billingDate?: number | null
  currentValue?: number | null
  /** What the account adds to its side's total (positive value or amount owed). Optional: an older backend omits it. */
  netWorthValue?: number
  /** Set only on archived accounts, which are listed only with includeArchived. */
  archivedAt?: string | null
  /** Latest reconcile against the bank; null when never reconciled. Optional: an older backend omits it. */
  reconciliation?: ReconciliationView | null
}

export interface ReconciliationView {
  /** yyyy-MM-dd the confirmed bank balance was for. */
  asOf: string
  balance: number
  /** RECHECK once something on or before asOf was added, changed or removed. */
  status: 'RECONCILED' | 'RECHECK'
  /** Transactions dated after asOf. */
  newSince: number
}

/** Bank, cash, wallets and cards; loans, investments and property keep their own balance updates. */
export const RECONCILABLE_TYPES: AccountType[] = ['BANK', 'CASH', 'DIGITAL_WALLET', 'CREDIT_CARD']

export interface ReconcileResult {
  matched: boolean
  asOf: string
  appBalance: number
  bankBalance: number
  /** bankBalance minus appBalance; 0 when matched. */
  difference: number
  ticked: number
}

/** Matching balance → ticks everything up to asOf. `adjust` books the difference first. */
export async function reconcileAccount(
  id: string,
  payload: { asOf: string; balance: number; adjust?: boolean }
): Promise<ReconcileResult> {
  const { data } = await api.post<ReconcileResult>(`/financial-accounts/${id}/reconcile`, payload)
  return data
}

export interface AccountListResponse {
  accounts: AccountSummary[]
  totalAssets: number
  totalLiabilities: number
  /** People balances, already inside totalAssets / totalLiabilities. Optional: an older backend omits them. */
  owedByPeople?: number
  owedToPeople?: number
  netWorth: number
}

export interface CreateAccountPayload {
  type: string
  name: string
  icon?: string
  subType?: string
  openingBalance?: number
  creditLimit?: number
  ownerMemberIds?: string[]
}

export interface UpdateAccountPayload {
  name?: string
  icon?: string
  ownerMemberIds?: string[]
  newBalance?: number
  categoryId?: string
  description?: string
  date?: string
  notes?: string
  creditLimit?: number
  billingDate?: number
  newOpeningBalance?: number
}

export async function listAccounts(
  type?: string,
  options: { includeArchived?: boolean } = {}
): Promise<AccountListResponse> {
  const params = {
    ...(type ? { type } : {}),
    ...(options.includeArchived ? { includeArchived: true } : {}),
  }
  const { data } = await api.get<AccountListResponse>('/financial-accounts', { params })
  return data
}

export async function createAccount(payload: CreateAccountPayload): Promise<AccountSummary> {
  const { data } = await api.post<AccountSummary>('/financial-accounts', payload)
  return data
}

export async function updateAccount(
  id: string,
  payload: UpdateAccountPayload
): Promise<AccountSummary> {
  const { data } = await api.patch<AccountSummary>(`/financial-accounts/${id}`, payload)
  return data
}

export async function archiveAccount(id: string): Promise<void> {
  await api.post(`/financial-accounts/${id}/archive`)
}

export interface BalancePoint {
  /** yyyy-MM */
  month: string
  /** The balance at that month's end. */
  balance: number
}

export async function getBalanceHistory(id: string, months: number): Promise<BalancePoint[]> {
  const { data } = await api.get<BalancePoint[]>(`/financial-accounts/${id}/balance-history`, {
    params: { months },
  })
  return data
}

export interface AccountCategorySpend {
  categoryId: string | null
  name: string
  icon: string | null
  spent: number
}

export async function getCategorySpend(
  id: string,
  dateFrom: string,
  dateTo: string
): Promise<AccountCategorySpend[]> {
  const { data } = await api.get<AccountCategorySpend[]>(
    `/financial-accounts/${id}/category-spend`,
    { params: { dateFrom, dateTo } }
  )
  return data
}
